import { Component } from '@theme/component';
import { prefersReducedMotion, clamp } from '@theme/utilities';

/** How many pixels the background image shifts for every pixel scrolled. */
const PARALLAX_SPEED = 0.25;
/** Hard cap, in pixels, on how far the background can shift in either direction. */
const MAX_SHIFT_PX = 120;

/**
 * A custom element that shifts a single background image vertically as the
 * section scrolls through the viewport, for a simple sense of depth. Generic
 * (single "bg" ref) so any section can drop it behind its content.
 *
 * @typedef {object} Refs
 * @property {HTMLImageElement} bg
 *
 * @extends Component<Refs>
 */
export class SectionParallaxMedia extends Component {
  requiredRefs = ['bg'];

  /** @type {number | null} */
  #rafId = null;
  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** Cached position/size, refreshed on connect/resize rather than read on
   *  every scroll frame. */
  #sectionTop = 0;
  #sectionHeight = 1;
  #resizeRafId = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#measure();
    window.addEventListener('resize', this.#handleResize, { passive: true });

    if (prefersReducedMotion()) return;

    this.#observer = new IntersectionObserver(this.#handleIntersect, { rootMargin: '20% 0px' });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this.#handleScroll);
    window.removeEventListener('resize', this.#handleResize);
    this.#observer?.disconnect();
    if (this.#rafId !== null) cancelAnimationFrame(this.#rafId);
    if (this.#resizeRafId) cancelAnimationFrame(this.#resizeRafId);
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #handleIntersect = (entries) => {
    const isVisible = entries.some((entry) => entry.isIntersecting);

    if (isVisible) {
      // Re-measure now rather than trusting the connect-time value: layout
      // may have shifted while this was still off-screen.
      this.#measure();
      window.addEventListener('scroll', this.#handleScroll, { passive: true });
      this.#handleScroll();
    } else {
      window.removeEventListener('scroll', this.#handleScroll);
    }
  };

  #handleScroll = () => {
    if (this.#rafId !== null) return;

    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#update();
    });
  };

  #handleResize = () => {
    if (this.#resizeRafId) return;

    this.#resizeRafId = requestAnimationFrame(() => {
      this.#resizeRafId = 0;
      this.#measure();
      this.#update();
    });
  };

  /** Reads layout once (forces a synchronous reflow) and caches it, instead
   *  of doing that read on every scroll frame. */
  #measure() {
    const rect = this.getBoundingClientRect();
    this.#sectionTop = rect.top + window.scrollY;
    this.#sectionHeight = rect.height || 1;
  }

  #update() {
    // Distance between the viewport's center and the section's center, so
    // the image shifts one way as the section enters and the other way as
    // it leaves — a symmetric parallax rather than a one-directional drift.
    const sectionCenter = this.#sectionTop + this.#sectionHeight / 2;
    const viewportCenter = window.scrollY + window.innerHeight / 2;
    const shiftPx = clamp((viewportCenter - sectionCenter) * PARALLAX_SPEED, -MAX_SHIFT_PX, MAX_SHIFT_PX);

    this.style.setProperty('--parallax-y', `${shiftPx}px`);
  }
}

if (!customElements.get('section-parallax-media')) {
  customElements.define('section-parallax-media', SectionParallaxMedia);
}
